// CustomDialog.jsx:
// Component that provides a dialog to show information, errors, warnings and confirmations.

// Copyright HS Analysis GmbH, 2021
// Author: Viktor Eberhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// Material UI imports
import { IconButton, Tooltip } from "@mui/material";
import { makeStyles } from "@mui/styles";
import classNames from "classnames";

// Icon imports
import { faInfo } from "@fortawesome/free-solid-svg-icons";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { faLayerGroup } from "@fortawesome/free-solid-svg-icons";
import { faQrcode } from "@fortawesome/free-solid-svg-icons";
import { faChartArea } from "@fortawesome/free-solid-svg-icons";
import { faRuler } from "@fortawesome/free-solid-svg-icons";
import { faStopwatch } from "@fortawesome/free-solid-svg-icons";
import { faTable } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { UnfoldMore } from "@mui/icons-material";
import { LinearScale } from "@mui/icons-material";
import { Map } from "@mui/icons-material";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";

const useStyles = makeStyles((theme) => ({
  button: {
    display: "inline-block",
    width: 40,
    height: 40,
    padding: 0,
    margin: 0,
    fontSize: 22,
  },
  buttonIcon: {
    verticalAlign: "-4px",
  },
  buttonChecked: {
    width: 40,
    color: theme.palette.primary.main,
  },
  buttonDisabled: {
    color: theme.palette.icon.disabled,
  },
}));

const ToggleButton = ({
  title,
  name,
  value,
  icon,
  onToggle,
  disabled = false,
  disableReason = "Currently not available",
}) => {
  const classes = useStyles();
  const displayTitle = disabled ? `${title}: ${disableReason}` : title;

  return (
    <Tooltip disableInteractive title={displayTitle} placement="top">
      <IconButton
        className={classNames(
          classes.button,
          value && classes.buttonChecked,
          disabled && classes.buttonDisabled
        )}
        onClick={() => {
          if (disabled) {
            window.showWarningSnackbar(displayTitle);
          } else {
            onToggle(name);
          }
        }}
        size="small"
      >
        {icon === "faChartArea" && <FontAwesomeIcon icon={faChartArea} />}
        {icon === "MapIcon" && <Map className={classes.buttonIcon} />}
        {icon === "faInfoCircle" && <FontAwesomeIcon icon={faInfoCircle} />}
        {icon === "faRuler" && <FontAwesomeIcon icon={faRuler} />}
        {icon === "faStopwatch" && <FontAwesomeIcon icon={faStopwatch} />}
        {icon === "faLayerGroup" && <FontAwesomeIcon icon={faLayerGroup} />}
        {icon === "faInfo" && <FontAwesomeIcon icon={faInfo} />}
        {icon === "LinearScaleIcon" && (
          <LinearScale
            className={classes.buttonIcon}
            style={{ transform: "rotate(90deg)" }}
          />
        )}
        {icon === "faTable" && <FontAwesomeIcon icon={faTable} />}
        {icon === "faQrcode" && <FontAwesomeIcon icon={faQrcode} />}
        {icon === "UnfoldMore" && (
          <UnfoldMore style={{ marginTop: -5, transform: "rotate(90deg)" }} />
        )}
        {icon === "ImageSearchIcon" && (
          <ImageSearchIcon className={classes.buttonIcon} />
        )}
      </IconButton>
    </Tooltip>
  );
};

ToggleButton.propTypes = {
  disabled: PropTypes.bool,
  disableReason: PropTypes.string,
  icon: PropTypes.string,
  key: PropTypes.string,
  name: PropTypes.string,
  onToggle: PropTypes.func,
  title: PropTypes.string,
  value: PropTypes.bool,
};

export default ToggleButton;
